import type { MultiRunIntroConfig } from 'featureFlags';

/**
 * Player-facing copy for the one-time multi-run introduction (EN-10791).
 * Jason owns the copy (2026-09-28).
 *
 * Two kinds, split on purpose (Keith, #4004 review):
 *   - TIMELESS copy lives here. It stays true however far the rollout has gone,
 *     and it is what renders when the flag's edition supplies nothing for a
 *     field, or supplies something invalid. The test: if the modal popped up a
 *     year from now with no flag payload behind it, would every sentence still
 *     be true? So no "coming soon", no "beta", no game-specific scope.
 *   - TIMELY copy (which games are live, what is coming, the beta) comes from
 *     the flag's edition. multiRunIntroEditions.ts holds the variations as
 *     written; featureFlags/multiRunIntroConfig.ts holds the rules.
 * Rules both kinds keep, from the EN-663 contract:
 *   - RUN IT strings use AC-5.2's casing ("RUN IT 1X/2X/3X"), never "twice",
 *     "thrice" or "three times".
 *   - "beta" appears in at most one place in the app: an edition's small
 *     print (AC-9.1).
 *   - Nothing prints the vote length. The window is configurable per table
 *     template, so "8 seconds" would be wrong on some tables.
 *   - The small print stays true to the server's eligibility rules: a
 *     voluntary all-in that got called, on a cash table (a blind-post all-in
 *     never initiates — Sevs' ruling, confirmed by QA on 2026-09-23).
 */
export const MULTI_RUN_INTRO_TIMELESS = {
  // The headline is a hook and its answer, set as two lines so a narrow
  // screen wraps inside one of them and never splits "Run / it".
  titleLines: ['One board not enough?', 'Run it 2X or 3X.'],
  availability: ['Live now on cash tables, every stake.'],
  smallPrint:
    "Offered on cash tables when you choose to go all in and get called — a blind that puts you all in doesn't count."
} as const;

/** The parts that change only with the feature itself: they follow the EN-663 rules. */
export const MULTI_RUN_INTRO_COPY = {
  cardHeading: 'How it works',
  cardTag: 'Decide in seconds',
  steps: [
    'All in and called, with cards still to come.',
    'The first player all in picks 1X, 2X or 3X.',
    'Everyone else in the hand agrees — or it runs once.'
  ],
  cardFoot: 'Pot splits across the runs',
  action: 'Browse tables',
  closeLabel: 'Close',
  chipsLabel: 'Run-count options'
} as const;

/** What the modal renders: an edition's copy merged over the timeless copy. */
export type MultiRunIntroContent = {
  /** The edition this is, or null for the timeless copy on its own (a preview). */
  edition: string | null;
  titleLines: readonly [string, string];
  availability: readonly string[];
  smallPrint: string;
};

export const resolveMultiRunIntroContent = (
  config: MultiRunIntroConfig | null
): MultiRunIntroContent => ({
  edition: config?.edition ?? null,
  titleLines: config?.titleLines ?? MULTI_RUN_INTRO_TIMELESS.titleLines,
  availability: config?.availability ?? MULTI_RUN_INTRO_TIMELESS.availability,
  smallPrint: config?.smallPrint ?? MULTI_RUN_INTRO_TIMELESS.smallPrint
});

/** The dialog's accessible name: the headline as one sentence pair. */
export const multiRunIntroTitle = (content: Pick<MultiRunIntroContent, 'titleLines'>): string =>
  content.titleLines.join(' ');
